import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { areEqual } from 'Utils/equalityChecks';
import { getQuickBooksAuthUrl, disconnectQuickBooks } from '../actions';
import type { QuickBooksStatusProps } from '../types';

import classes from './quickBooksStatus.module.css';

// Surface the failing request path + HTTP status instead of collapsing every
// failure cause (404 path, 422 body, missing SSO) into one generic message.
const reportQbError = (event: string, err: unknown) => {
  const path = axios.isAxiosError(err) ? err.config?.url : undefined;
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  Sentry.addBreadcrumb({
    category: 'quickbooks',
    level: 'error',
    message: event,
    data: { path, status },
  });
  // eslint-disable-next-line no-console
  console.error(`QuickBooks ${event} failed:`, { path, status }, err);
};

function QuickBooksStatus({ locationId, ssoToken, authStatus, authError, loading, onAuthChange }: QuickBooksStatusProps) {
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  // Clear any pending popup-poll interval on unmount.
  useEffect(
    () => () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    },
    []
  );

  const handleConnect = useCallback(async () => {
    try {
      setConnecting(true);
      setError(null);
      const { authUrl } = await getQuickBooksAuthUrl(locationId, ssoToken);
      // Open QuickBooks OAuth in a new window
      const popup = window.open(authUrl, '_blank', 'width=600,height=700');
      if (!popup) {
        setError('Popup blocked. Allow popups for this site and try again.');
        return;
      }
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
      // The OAuth callback is handled entirely server-side and posts nothing back
      // to this window, so detect completion by polling popup.closed, then re-check
      // the connection status so the Status/Config/Log tabs update.
      pollRef.current = window.setInterval(() => {
        if (popup.closed) {
          if (pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
          }
          onAuthChange();
        }
      }, 800);
    } catch (err) {
      setError('Failed to get QuickBooks authorization URL');
      reportQbError('qb_auth_url', err);
    } finally {
      setConnecting(false);
    }
  }, [locationId, ssoToken, onAuthChange]);

  const handleDisconnect = useCallback(async () => {
    if (!window.confirm('Are you sure you want to disconnect QuickBooks? This will stop syncing invoices and payments.')) {
      return;
    }
    try {
      setDisconnecting(true);
      setError(null);
      await disconnectQuickBooks(locationId, ssoToken);
      // Refresh the shared auth state so the Config/Log tabs update too.
      await onAuthChange();
    } catch (err) {
      setError('Failed to disconnect QuickBooks');
      reportQbError('qb_disconnect', err);
    } finally {
      setDisconnecting(false);
    }
  }, [locationId, ssoToken, onAuthChange]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Checking QuickBooks connection...</p>
      </div>
    );
  }

  // A failed status check is distinct from "not connected" — show an error/retry
  // state and do not fall through to the disconnected UI.
  if (authError && !authStatus) {
    return (
      <div className={classes.container}>
        <h3 className="mb-4">QuickBooks Connection Status</h3>
        <div className="alert alert-danger" role="alert">
          {authError}
          <button type="button" className="btn btn-link btn-sm" onClick={onAuthChange}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h3 className="mb-4">QuickBooks Connection Status</h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={onAuthChange}
          >
            Retry
          </button>
        </div>
      )}

      {authStatus?.connected ? (
        <div className={classes.statusCard}>
          <div className={classes.statusHeader}>
            <span className={classes.statusIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#28a745" />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className={classes.statusText}>QuickBooks Connected</span>
          </div>
          <p className="text-muted mb-3">
            Your location is successfully connected to QuickBooks and ready to create invoices and track payments.
          </p>
          {authStatus.companyName && (
            <div className={classes.companyInfo}>
              <strong>Connected Company:</strong>
{' '}
{authStatus.companyName}
              {authStatus.realmId && (
                <>
                  <br />
                  <strong>QuickBooks ID:</strong>
{' '}
{authStatus.realmId}
                </>
              )}
            </div>
          )}
          <div className="mt-4 d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onAuthChange}
            >
              Refresh Status
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect QuickBooks'}
            </button>
          </div>
        </div>
      ) : (
        <div className={classes.statusCard}>
          <div className={classes.statusHeader}>
            <span className={classes.statusIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#dc3545" />
                <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className={classes.statusText}>QuickBooks Not Connected</span>
          </div>
          <p className="text-muted mb-3">
            Your location is not connected to QuickBooks. Connect now to start creating invoices automatically.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Connecting...
              </>
            ) : (
              'Connect to QuickBooks'
            )}
          </button>

          <div className={classes.infoBox}>
            <strong>What happens when you connect:</strong>
            <ul className="mb-0 mt-2">
              <li>Your RocketCRM opportunities will automatically create QuickBooks invoices</li>
              <li>Customer information will sync between systems</li>
              <li>Payment status will update opportunities in RocketCRM</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

const QuickBooksStatusMemo = memo(QuickBooksStatus, areEqual);

export { QuickBooksStatusMemo as QuickBooksStatus };
